import React, { useState, useEffect } from 'react';
import { Tag, Spin, Collapse } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';

const { Panel } = Collapse;

// 7大素质维度配置
const TRAIT_CONFIG = {
  mobility: { name: '活动度', icon: '🤸', color: '#52c41a', bg: '#f6ffed' },
  strengthPerformance: { name: '力量', icon: '💪', color: '#fa8c16', bg: '#fff7e6' },
  control: { name: '控制', icon: '🎯', color: '#1890ff', bg: '#e6f7ff' },
  stability: { name: '稳定', icon: '⚖️', color: '#722ed1', bg: '#f9f0ff' },
  coordination: { name: '协调', icon: '🔄', color: '#eb2f96', bg: '#fff0f6' },
  agility: { name: '敏捷', icon: '⚡', color: '#13c2c2', bg: '#e6fffb' },
  powerPerformance: { name: '爆发', icon: '🚀', color: '#f5222d', bg: '#fff1f0' },
};

// 特征中文名映射
const FEATURE_LABELS = {
  jointRom: '关节活动范围',
  minAngle: '最小角度',
  maxAngle: '最大角度',
  targetSignalRange: '目标信号范围',
  normalizedMaxDisplacement: '归一化位移',
  eccentricDuration: '离心时长',
  concentricDuration: '向心时长',
  eccentricConcentricDurationRatio: '离/向时长比',
  concentricMinAngularVelocity: '向心最低角速度',
  phaseCompleteness: '阶段完整性',
  phaseConsistency: '阶段一致性',
  cycleDurationCv: '周期时长变异',
  cadence: '动作频率',
  bodyCenterSwayX: '重心横向摆动',
  bodyCenterSwayY: '重心纵向摆动',
  trunkTiltStd: '躯干倾角标准差',
  trunkTiltRange: '躯干倾角范围',
  leftRightTimingDiff: '左右时序差',
  phaseSync: '阶段同步性',
  peakAngularVelocity: '峰值角速度',
  peakAcceleration: '峰值加速度',
  romScore: '活动度得分',
  holdQuality: '保持质量',
  avgRom: '平均ROM',
  avgCycleDurationMs: '平均周期时长',
  tempoConsistencyCv: '节奏一致性',
  romConsistencyCv: 'ROM一致性',
  romTrend: 'ROM趋势',
  avgEccentricMs: '平均离心时长',
  avgConcentricMs: '平均向心时长',
  avgEccentricConcentricRatio: '离/向比',
  postureScore: '姿态分',
  holdScore: '保持分',
  validDuration: '有效时长',
  holdCount: '保持次数',
  breakCount: '中断次数',
};

const formatValue = (val, unit) => {
  if (val == null) return '--';
  if (typeof val === 'number') {
    if (unit === 'ms' && val >= 1000) return (val / 1000).toFixed(2) + 's';
    if (unit === 'ms') return val.toFixed(0) + 'ms';
    if (unit === '°' || unit === 'degree') return val.toFixed(1) + '°';
    if (unit === '%') return val.toFixed(1) + '%';
    if (unit === 'norm') return val.toFixed(3);
    if (unit === 'ratio') return val.toFixed(2);
    if (unit === 'count/min') return val.toFixed(1) + '次/分';
    if (unit === 'degree/s') return val.toFixed(1) + '°/s';
    if (unit === 'degree/s²') return val.toFixed(1) + '°/s²';
    return val.toFixed(2);
  }
  return String(val);
};

const FeatureDetailCards = ({ layer3Url }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!layer3Url) return;
    setLoading(true);
    fetch(layer3Url)
      .then(res => res.json())
      .then(json => {
        setData(json);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [layer3Url]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 20, background: '#fafafa', borderRadius: 8 }}><Spin size="small" /></div>;
  }

  if (!data) return null;

  const features = data.features || {};
  const overallFeatures = features.overall || [];
  const unitFeatures = features.perUnit || [];

  // 按素质维度分组特征
  const groupByTrait = (featureList) => {
    const groups = {};
    featureList.forEach(f => {
      const trait = f.trait || f.qualityDimension || 'other';
      if (!groups[trait]) groups[trait] = [];
      groups[trait].push(f);
    });
    return groups;
  };

  const overallGroups = groupByTrait(overallFeatures);

  // 如果数据结构是直接的 key-value
  const renderDirectFeatures = () => {
    const allFeatures = [];

    // 只展示 overall 特征（跨周期汇总），不展示 byUnit（逐周期重复指标）
    if (Array.isArray(overallFeatures)) {
      overallFeatures.forEach(f => {
        if (f.value != null && f.value !== '') {
          allFeatures.push({
            name: f.featureName || f.name,
            displayName: f.displayName || '',
            value: f.value,
            unit: f.unit || '',
            scope: 'overall',
          });
        }
      });
    }

    return allFeatures;
  };

  const allFeatures = renderDirectFeatures();

  if (allFeatures.length === 0) {
    // 尝试从 summary 提取
    const summary = data.summary || {};
    const summaryItems = [];

    if (summary.durationResult) {
      const dr = summary.durationResult;
      if (dr.postureScore != null) summaryItems.push({ name: 'postureScore', value: dr.postureScore, unit: '' });
      if (dr.holdScore != null) summaryItems.push({ name: 'holdScore', value: dr.holdScore, unit: '' });
      if (dr.stabilityMetrics) {
        Object.entries(dr.stabilityMetrics).forEach(([k, v]) => {
          summaryItems.push({ name: k, value: v, unit: k.includes('Sway') ? 'norm' : '°' });
        });
      }
    }

    if (summary.angleCycleResult) {
      const acr = summary.angleCycleResult;
      Object.entries(acr).forEach(([k, v]) => {
        if (typeof v === 'number') {
          const unitMap = {
            avgRom: '°', avgCycleDurationMs: 'ms', cadence: 'count/min',
            tempoConsistencyCv: '%', romConsistencyCv: '%',
            avgEccentricMs: 'ms', avgConcentricMs: 'ms',
          };
          summaryItems.push({ name: k, value: v, unit: unitMap[k] || '' });
        }
      });
    }

    if (summaryItems.length === 0) return null;

    return (
      <div style={{ background: '#fff', borderRadius: 8, padding: '14px 16px', marginBottom: 16, border: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <BarChartOutlined style={{ color: '#1890ff', marginRight: 6 }} />
          <span style={{ fontWeight: 600, fontSize: 14 }}>特征详情</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {summaryItems.map((item, idx) => (
            <div key={idx} style={{ background: '#fafafa', borderRadius: 6, padding: '8px 12px', border: '1px solid #f0f0f0' }}>
              <div style={{ fontSize: 11, color: '#888' }}>{FEATURE_LABELS[item.name] || item.name}</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#333', marginTop: 2 }}>
                {formatValue(item.value, item.unit)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: '14px 16px', marginBottom: 16, border: '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <BarChartOutlined style={{ color: '#1890ff', marginRight: 6 }} />
        <span style={{ fontWeight: 600, fontSize: 14 }}>特征详情</span>
        <Tag color="blue" style={{ marginLeft: 8 }}>{allFeatures.length} 个特征</Tag>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {allFeatures.map((item, idx) => (
          <div key={idx} style={{ background: '#fafafa', borderRadius: 6, padding: '8px 12px', border: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 11, color: '#888' }}>{item.displayName || FEATURE_LABELS[item.name] || item.name}</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#333', marginTop: 2 }}>
              {formatValue(item.value, item.unit)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeatureDetailCards;
